import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { X, Fuel, DollarSign, Loader2, Upload, FileText } from 'lucide-react';
import { client } from '@/api/client';
import { toast } from 'sonner';
import { computeLoadGross } from '@/lib/invoiceHelpers';
import { pickPhotoNative, isNativePlatform } from '@/lib/nativeCamera';

const PERSONAL_WEEKLY_CAP = 200;

export default function FuelAdvanceModal({
  advanceType = 'fuel',
  load,
  user,
  weeklyPersonalAdvanceTotal = 0,
  onClose,
  onSubmit,
  submitting,
}) {
  const isPersonal = advanceType === 'personal';
  const [amount, setAmount] = useState('');
  const [notes, setNotes] = useState('');
  const [bolUrl, setBolUrl] = useState('');
  const [uploadingBol, setUploadingBol] = useState(false);

  const maxFuelAdvance = load ? computeLoadGross(load) * 0.4 : 0;
  const remainingPersonal = Math.max(0, PERSONAL_WEEKLY_CAP - weeklyPersonalAdvanceTotal);

  const handleBolUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingBol(true);
    toast.info('Uploading BOL...');
    const { file_url } = await client.integrations.Core.UploadFile({ file });
    setBolUrl(file_url);
    setUploadingBol(false);
    toast.success('BOL uploaded.');
  };

  // Native app builds get a direct camera/gallery prompt instead of the
  // browser's generic file picker.
  const handleNativeBolCapture = async () => {
    try {
      const file = await pickPhotoNative('bol');
      if (!file) return; // driver cancelled
      setUploadingBol(true);
      toast.info('Uploading BOL...');
      const { file_url } = await client.integrations.Core.UploadFile({ file });
      setBolUrl(file_url);
      toast.success('BOL uploaded.');
    } catch {
      toast.error('Could not capture photo.');
    } finally {
      setUploadingBol(false);
    }
  };

  const handleSubmit = () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) return;
    if (isPersonal) {
      if (amt > remainingPersonal) {
        toast.error(`Amount exceeds your remaining personal advance for this week ($${remainingPersonal.toFixed(2)}).`, { duration: 5000 });
        return;
      }
      onSubmit({ amount: amt, notes });
      return;
    }
    if (!bolUrl) { toast.error('Please upload a BOL before requesting an advance.'); return; }
    if (amt > maxFuelAdvance) {
      toast.error(`Amount exceeds the maximum fuel advance of $${maxFuelAdvance.toFixed(2)} (40% of gross).`, { duration: 5000 });
      return;
    }
    onSubmit({ amount: amt, notes, bol_url: bolUrl });
  };

  const canSubmit = !!amount && parseFloat(amount) > 0 && (isPersonal || !!bolUrl) && !submitting;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-card border border-border rounded-2xl shadow-xl w-full max-w-md z-10">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div className="flex items-center gap-2">
            {isPersonal ? <DollarSign className="w-5 h-5 text-primary" /> : <Fuel className="w-5 h-5 text-primary" />}
            <h2 className="font-semibold text-foreground">{isPersonal ? 'Request Personal Advance' : 'Request Fuel Advance'}</h2>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-4">
          {isPersonal ? (
            <div className="px-3 py-2 rounded-lg bg-amber-500/10 border border-amber-500/20 text-xs text-amber-400">
              Remaining this week: <span className="font-bold">${remainingPersonal.toFixed(2)}</span> (max $200/week)
            </div>
          ) : (
            <div className="px-3 py-2 rounded-lg bg-amber-500/10 border border-amber-500/20 text-xs text-amber-400">
              Load #{load.load_number} — Max advance: <span className="font-bold">${maxFuelAdvance.toFixed(2)}</span> (40% of gross)
            </div>
          )}

          <div>
            <label className="text-xs text-muted-foreground block mb-1">Amount ($) <span className="text-red-400">*</span></label>
            <input
              type="number"
              value={amount}
              onChange={e => setAmount(e.target.value)}
              placeholder="Enter amount"
              className="w-full px-3 py-2 rounded-lg bg-transparent border border-input text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/50"
              autoFocus
            />
          </div>

          {!isPersonal && (
            <div>
              <label className="text-xs text-muted-foreground block mb-1">BOL Document <span className="text-red-400">*</span></label>
              <label
                onClick={(e) => {
                  if (isNativePlatform()) {
                    e.preventDefault();
                    handleNativeBolCapture();
                  }
                }}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border cursor-pointer transition-colors ${bolUrl ? 'border-green-500/40 bg-green-500/5' : 'border-border bg-muted/40 hover:border-primary/40'}`}
              >
                <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png" onChange={handleBolUpload} disabled={uploadingBol} />
                {uploadingBol ? <Loader2 className="w-4 h-4 animate-spin text-muted-foreground flex-shrink-0" /> : bolUrl ? <FileText className="w-4 h-4 text-green-400 flex-shrink-0" /> : <Upload className="w-4 h-4 text-muted-foreground flex-shrink-0" />}
                <span className={`text-sm truncate ${bolUrl ? 'text-green-400' : 'text-muted-foreground'}`}>
                  {uploadingBol ? 'Uploading…' : bolUrl ? 'BOL uploaded — click to replace' : 'Upload BOL (PDF, JPG, PNG)'}
                </span>
              </label>
            </div>
          )}

          <div>
            <label className="text-xs text-muted-foreground block mb-1">Notes (optional)</label>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Any additional details..."
              rows={3}
              className="w-full px-3 py-2 rounded-lg bg-transparent border border-input text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/50 resize-none"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex gap-3 px-6 py-4 border-t border-border">
          <Button variant="outline" onClick={onClose} className="flex-1 border-border" disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="flex-1 bg-primary text-primary-foreground"
          >
            {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting...</> : 'Submit Request'}
          </Button>
        </div>
      </div>
    </div>
  );
}
